<template>
  <div
    :class="$style.wrapper"
    :style="wrapperStyle"
    ref="wrap"
    data-header="true"
  >
    <img
      class="logo"
      src="@/assets/img/logo.svg"
      alt="Logo"
      role="button"
      tabindex="0"
      @click="goHome"
      @keydown.enter="goHome"
    />
    <button
      v-if="isTelegramWebApp"
      ref="menuBtnRef"
      type="button"
      :class="$style.menuBtn"
      aria-label="Меню"
      @click="menuOpen = !menuOpen"
      @blur="onMenuBlur"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="6" r="1.5" fill="currentColor"/>
        <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
        <circle cx="12" cy="18" r="1.5" fill="currentColor"/>
      </svg>
    </button>
    <Transition name="menu-fade">
      <div v-if="menuOpen" :class="$style.menuDrop" ref="menuRef">
        <button
          type="button"
          :class="$style.menuItem"
          @mousedown.prevent="addToHomeScreen"
        >
          <span :class="$style.menuIcon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="4" y="4" width="16" height="16" rx="2"/>
              <path d="M12 8v8M8 12h8"/>
            </svg>
          </span>
          Добавить на экран «Домой»
        </button>
      </div>
    </Transition>
  </div>
</template>

<script>
export default {
  name: "Header",
  data() {
    return {
      localHeaderHeight: 56,
      topPad: 0,
      isFullscreen: false,
      menuOpen: false,
    }
  },
  computed: {
    isTelegramWebApp() {
      return typeof window !== "undefined" && !!window.Telegram?.WebApp?.initData
    },
  },
  methods: {
    onMenuBlur() {
      setTimeout(() => {
        this.menuOpen = false
      }, 150)
    },
    addToHomeScreen() {
      this.menuOpen = false
      const tg = window.Telegram?.WebApp
      if (!tg) return
      if (typeof tg.addToHomeScreen === "function") {
        tg.addToHomeScreen()
        return
      }
      const ua = navigator.userAgent || ""
      const isIOS = /iPad|iPhone|iPod/i.test(ua)
      const msg = isIOS
        ? "Нажмите кнопку «Поделиться» (□↑) в нижней панели браузера, затем выберите «На экран «Домой»»."
        : "Откройте меню браузера (три точки) и выберите «Добавить на главный экран» или «Установить приложение»."
      tg.showAlert?.(msg) || alert(msg)
    },
    goHome() {
      // Отключаем навигацию на страницах регистрации
      const registerRoutes = [
        "register",
        "SelectDeliveryTime",
        "DeliveryPicker",
        "FillingData",
        "CheckEmail",
      ]
      if (registerRoutes.includes(this.$route?.name)) {
        return
      }
      if (this.$route?.path === "/") {
        return
      }
      this.$router.push({ path: "/" })
    },
  },
  computed: {
    wrapperStyle() {
      return {
        "--top-pad": `${this.topPad}px`,
        "--local-header-height": `${this.localHeaderHeight}px`,
      }
    },
  },
  mounted() {
    const tg = window?.Telegram?.WebApp

    const ensureVisible = () => {
      const el = this.$refs.wrap
      if (!el) return

      // Всегда принудительно устанавливаем стили для гарантии видимости
      el.style.setProperty("display", "flex", "important")
      el.style.setProperty("visibility", "visible", "important")
      el.style.setProperty("opacity", "1", "important")
      el.style.setProperty("position", "fixed", "important")
      el.style.setProperty("top", "0", "important")
      el.style.setProperty("left", "0", "important")
      el.style.setProperty("right", "0", "important")
      el.style.setProperty("z-index", "10000", "important")
    }

    const measure = () => {
      const h = this.$refs.wrap?.getBoundingClientRect?.().height || 0
      document.documentElement.style.setProperty(
        "--app-header-height",
        `${h}px`
      )
      ensureVisible()
    }

    const tryUpdateFromTG = () => {
      const safeTop =
        typeof tg?.safeAreaInset?.top === "number" ? tg.safeAreaInset.top : null
      const ua = navigator.userAgent || ""
      const isIOS = /iPad|iPhone|iPod/i.test(ua)
      const isAndroid = /Android/i.test(ua)
      const headerVisualHalf = isAndroid ? 28 : 26

      if (!this.isFullscreen) {
        const safe = safeTop ?? (isIOS ? 20 : 0)
        this.topPad = Math.max(
          0,
          safe + headerVisualHalf - this.localHeaderHeight / 2
        )
      } else {
        // При fullscreen все равно устанавливаем минимальный padding
        // чтобы Header не скрывался за нативным header Telegram
        this.topPad = safeTop ?? (isIOS ? 20 : 0)
      }

      this.$nextTick(() => {
        measure()
        ensureVisible()
      })
    }

    // Сначала гарантируем видимость
    ensureVisible()

    this.isFullscreen = !!tg?.isFullscreen
    tryUpdateFromTG()

    // Вызываем measure для установки высоты
    this.$nextTick(() => {
      ensureVisible()
      measure()
      ensureVisible()
    })

    // Дополнительная проверка после небольшой задержки (для телефонов)
    setTimeout(() => {
      ensureVisible()
      if (tg) {
        this.isFullscreen = !!tg.isFullscreen
        tryUpdateFromTG()
      }
    }, 200)

    // MutationObserver для отслеживания изменений стилей
    if (this.$refs.wrap) {
      const observer = new MutationObserver(() => {
        ensureVisible()
      })
      observer.observe(this.$refs.wrap, {
        attributes: true,
        attributeFilter: ["style", "class"],
      })
      this._observer = observer
    }

    // Периодически проверяем видимость (fallback)
    this._visibilityCheck = setInterval(() => {
      ensureVisible()
    }, 500)

    try {
      tg?.onEvent?.("fullscreenChanged", ({ isFullscreen }) => {
        this.isFullscreen = !!isFullscreen
        tryUpdateFromTG()
        ensureVisible()
      })
      tg?.onEvent?.("viewportChanged", () => {
        tryUpdateFromTG()
        ensureVisible()
      })
    } catch (e) {
      console.warn("TG event attach failed", e)
    }

    this._onClickOutside = (e) => {
      const menu = this.$refs.menuRef
      const btn = this.$refs.menuBtnRef
      if (this.menuOpen && menu && !menu.contains(e.target) && btn && !btn.contains(e.target)) {
        this.menuOpen = false
      }
    }
    document.addEventListener("click", this._onClickOutside)

    const handleResize = () => {
      measure()
      ensureVisible()
      tryUpdateFromTG()
    }

    const handleOrientationChange = () => {
      // Небольшая задержка для завершения поворота
      setTimeout(() => {
        ensureVisible()
        measure()
        tryUpdateFromTG()
        // Дополнительная проверка после поворота
        setTimeout(() => {
          ensureVisible()
          measure()
          tryUpdateFromTG()
        }, 300)
      }, 100)
    }

    window.addEventListener("resize", handleResize)
    window.addEventListener("orientationchange", handleOrientationChange)

    this._handleResize = handleResize
    this._handleOrientationChange = handleOrientationChange

    this._cleanup = () => {
      document.removeEventListener("click", this._onClickOutside)
      window.removeEventListener("resize", this._handleResize)
      window.removeEventListener(
        "orientationchange",
        this._handleOrientationChange
      )
      if (this._observer) {
        this._observer.disconnect()
      }
      if (this._visibilityCheck) {
        clearInterval(this._visibilityCheck)
      }
    }
  },
  unmounted() {
    if (typeof this._cleanup === "function") {
      this._cleanup()
    }
  },
}
</script>

<style lang="scss" module>
.wrapper {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 10000 !important;
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
  justify-content: center;
  align-items: center;
  padding-top: var(--top-pad, 0px);
  padding-left: 16px;
  padding-right: 16px;
  min-height: var(--local-header-height, 56px);
  background: #fff !important;
}
.logo {
  max-height: 28px;
  height: auto;
  width: auto;
  display: block;
  cursor: pointer;
}
.menuBtn {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  padding: 8px;
  margin: 0;
  border: none;
  background: none;
  color: #000;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.menuDrop {
  position: absolute;
  top: 100%;
  right: 8px;
  margin-top: 4px;
  min-width: 220px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  padding: 6px 0;
  z-index: 10001;
}
.menuItem {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  font-size: 15px;
  color: #000;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}
.menuItem:hover,
.menuItem:active {
  background: rgba(0, 0, 0, 0.06);
}
.menuIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #666;
}
.menuIcon svg {
  display: block;
}
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.15s ease;
}
.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
}
</style>
